import client from './client';
import type { AnalyticsOverview, TimelineData, DepartmentStats } from '../types';

// The types below mirror models/analytics_comparison.go's
// ComparisonAnalytics/ComparisonWindow/ComparisonDefinitions/
// ComparisonSlice/RepeatClickTrendPoint. See that file's doc comments for
// the exact denominator/attribution/repeat-click/scanner-labeling rules
// summarized in `definitions`.
export interface ComparisonWindow {
  start: string;
  end: string;
  days: number;
}

export interface ComparisonDefinitions {
  recipient_denominator: string;
  report_attribution: string;
  median_time_to_report: string;
  repeat_click: string;
  missing_data: string;
  scanner_label: string;
}

export interface ComparisonSlice {
  key: string;
  label: string;
  campaign_id?: number;
  recipient_count: number;
  reported_count: number;
  report_rate: number;
  median_report_seconds: number | null;
  missing_report_time_count: number;
  clicked_recipient_count: number;
  repeat_click_recipient_count: number;
  click_event_count: number;
  suspected_scanner_click_count: number;
}

export interface RepeatClickTrendPoint {
  date: string;
  repeat_click_recipients: number;
  repeat_click_events: number;
  suspected_scanner_clicks: number;
}

export interface TrendData {
  date: string;
  open_rate: number;
  click_rate: number;
  submit_rate: number;
}

export interface RiskScoreBreakdown {
  score: number;
  level: 'Critical' | 'High' | 'Medium' | 'Low' | 'N/A';
  click_rate: number;
  submit_rate: number;
  report_rate: number;
  recommendations: string[];
}

export interface ComparisonAnalytics {
  window: ComparisonWindow;
  definitions: ComparisonDefinitions;
  campaigns: ComparisonSlice[];
  cohorts: ComparisonSlice[];
  repeat_click_trend: RepeatClickTrendPoint[];
}

export const analyticsApi = {
  getOverview: () =>
    client.get<AnalyticsOverview>('/analytics/overview'),

  getTimeline: (campaignId?: number) =>
    client.get<TimelineData[]>(
      campaignId ? `/analytics/campaigns/${campaignId}/timeline` : '/analytics/timeline'
    ),

  getDepartments: () =>
    client.get<DepartmentStats[]>('/analytics/departments'),

  getTrends: (period: string = '30d') =>
    client.get<TrendData[]>('/analytics/trends', { params: { period } }),

  getRiskScore: () =>
    client.get<RiskScoreBreakdown>('/analytics/risk-score'),

  getComparison: (campaignIds?: number[], windowDays?: number) =>
    client.get<ComparisonAnalytics>('/analytics/comparison', {
      params: {
        campaign_ids: campaignIds && campaignIds.length > 0 ? campaignIds.join(',') : undefined,
        window_days: windowDays,
      },
    }),

  export: (format: 'csv' | 'pdf' | 'xlsx', campaignId?: number) =>
    client.get<Blob>('/analytics/export', {
      params: { format, campaign_id: campaignId },
      responseType: 'blob',
    }),
};
